import { type FormEvent, useEffect, useRef, useState } from "react";

/** The API refuses anything shorter, and repeating the rule here saves a round trip. */
const MINIMUM_REASON = 10;

export interface ConfirmActionProps {
  /** What the button says once a reason has been given. */
  confirmLabel: string;
  danger?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  /** True while the request is in flight, so the dialog cannot be submitted twice. */
  pending: boolean;
  /** Override when the server applies a stricter audit-reason rule to this action. */
  minimumReason?: number;
  /** Optional server-side ceiling for bounded audit fields. */
  maximumReason?: number;
  /** One sentence on what is about to happen, in the operator's terms rather than the API's. */
  prompt: string;
  title: string;
}

/**
 * The gate in front of anything that changes a player's standing.
 *
 * The reason is mandatory and at least ten characters because it is what an investigator reads a
 * year later — "per policy" is not an answer to anything they will be asking. The rule belongs to
 * the server; repeating it here means a too-short reason costs no round trip, and the operator
 * finds out while they are still typing rather than after they have committed.
 */
export function ConfirmAction({
  confirmLabel,
  danger = false,
  error,
  onCancel,
  onConfirm,
  pending,
  minimumReason = MINIMUM_REASON,
  maximumReason,
  prompt,
  title,
}: ConfirmActionProps) {
  const [reason, setReason] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);
  const dialog = useRef<HTMLFormElement>(null);

  /**
   * Whatever had focus when this dialog was opened, captured during the first render.
   *
   * Read here rather than inside an effect, because by the time effects run the effect below has
   * already moved focus into the reason field — so an effect asking `document.activeElement` would
   * capture the textarea and, on unmount, try to focus an element that no longer exists.
   */
  const opener = useRef<Element | null>(null);
  if (opener.current === null) opener.current = document.activeElement;

  // The cursor belongs in the field the operator has to fill, not on the button they might hit by
  // reflex.
  useEffect(() => field.current?.focus(), []);

  /**
   * Put focus back where it came from when the dialog closes.
   *
   * Without this, dismissing drops focus onto the document body and the next Tab starts from the
   * top of the page — so an operator who cancelled a ban has to walk the whole table again to get
   * back to the row they were on.
   */
  useEffect(() => {
    const returnTo = opener.current;
    return () => {
      if (returnTo instanceof HTMLElement && returnTo.isConnected) returnTo.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel();
        return;
      }

      /**
       * Keep Tab inside the dialog.
       *
       * The dialog is modal in intent but not in the DOM: the page behind it is still focusable, so
       * without this an operator can Tab past Cancel onto Force logout and press it while a ban
       * confirmation is still open — acting on a control they cannot see, having answered a
       * question they never got to.
       */
      if (event.key !== "Tab" || !dialog.current) return;

      const focusable = dialog.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      // Wrapped by hand, because the browser's own order runs off the end of the dialog and into
      // the page behind it.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const tooShort = reason.trim().length < minimumReason;
  const tooLong = maximumReason !== undefined && reason.trim().length > maximumReason;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!tooShort && !tooLong && !pending) onConfirm(reason.trim());
  }

  return (
    // The backdrop is a real button rather than a div with a handler: it is genuinely clickable, so
    // it should be reachable and announced as such. Only the backdrop cancels — a stray click
    // inside the dialog must not discard a reason somebody has half-written.
    <div className="scrim">
      <button
        aria-label="Dismiss without acting"
        className="scrim-hit"
        onClick={onCancel}
        // Out of the tab order, because it is inside the trap and would otherwise be a stop between
        // Cancel and the reason field that dismisses the dialog when pressed. Still clickable, and
        // Escape does the same thing from the keyboard.
        tabIndex={-1}
        type="button"
      />
      <form
        aria-labelledby="confirm-title"
        // `role="dialog"` names the thing, `aria-modal` says the rest of the page is not available
        // while it is open — which is what makes a screen reader stop reading the table behind it.
        aria-modal="true"
        className="dialog"
        onSubmit={submit}
        ref={dialog}
        role="dialog"
      >
        <div className="dialog-head">
          <span className="heading-20" id="confirm-title">
            {title}
          </span>
          <p className="label-13 dim" style={{ margin: "4px 0 0" }}>
            {prompt}
          </p>
        </div>

        <div className="dialog-body">
          <div className="field">
            <label htmlFor="reason">Reason</label>
            <textarea
              // Named by its label rather than by its placeholder: a placeholder disappears the
              // moment somebody types, and a screen reader should not lose the field's name with it.
              aria-label="Reason"
              className="input textarea"
              id="reason"
              maxLength={maximumReason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="What happened, in a sentence."
              ref={field}
              rows={3}
              value={reason}
            />
            <span className="hint">
              {tooShort
                ? minimumReason === 1
                  ? "Reason is required."
                  : `At least ${minimumReason} characters — this is what an investigator reads later.`
                : "Recorded against the account and in the audit log."}
            </span>
          </div>

          {error ? (
            <p className="banner" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <div className="dialog-foot">
          <button className="btn btn-quiet" disabled={pending} onClick={onCancel} type="button">
            Cancel
          </button>
          <button
            className={danger ? "btn btn-danger" : "btn"}
            disabled={tooShort || tooLong || pending}
            type="submit"
          >
            {pending ? "Working…" : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
